/* eslint-disable no-case-declarations */
<template>
  <VCard ref="datesSelection">
    <ValidationObserver ref="dateForm" v-slot="{ invalid }" name="dateForm">
      <ValidationProvider
        ref="date"
        name="date"
        :rules="{ required: true }"
        mode="eager"
      >
        <VDatePicker
          :value="date"
          full-width
          :show-current="false"
          data-test="datePicker"
          :locale="getCurrentLang"
          scrollable
          :class="isCustomHeightDatePickerHeight ? 'date-picker body-1' : ''"
          calendar-class="my-datepicker_calendar"
          firstDayOfWeek="1"
          :allowedDates="allowedDates"
          :min="minDate"
          :max="maxDate"
          color="brandingPrimary"
          @change="updateDate"
        >
          <slot name="customDefaultHeader">
            <span v-if="!date" class="date__picker-custom-header"
              ><span class="error--text" aria-hidden="true">* </span>{{
                isRoundTrip
                  ? $t("bookingForm.dateSelection.selectOutwardDate")
                  : $t("bookingForm.dateSelection.selectDate")
              }}
            </span>
          </slot>
          <VBtn
            class="close-button mx-2"
            fab
            small
            @click="$emit('closeDialog')"
          >
            <VIcon> mdi-close </VIcon>
          </VBtn>
        </VDatePicker>
      </ValidationProvider>
      <template v-if="date">
        <VRow class="dialog-row pl-8 pb-4">
          <slot>
            <CustomBtnToggle
              :value="type"
              :items="dateTypeItems"
              @change="updateDateType"
            />
          </slot>
        </VRow>
        <VRow
          class="dialog-row pl-10"
          :class="{ 'pb-6': isWorkingHoursRoundTheClock }"
        >
          <VCol cols="10" class="p-0">
            <VMenu
              ref="menu"
              v-model="timePickerMenuOpen"
              :close-on-content-click="false"
              :nudge-top="180"
              :nudge-right="18"
              :value="time"
              transition="scale-transition"
              offset-y
              max-width="276px"
              min-width="276px"
              @change="updateTime"
            >
              <template #activator="{ on, attrs }">
                <ValidationProvider
                  v-slot="{ errors, ariaInput }"
                  ref="minutesInput"
                  name="minutesInput"
                  mode="aggressive"
                  :rules="{
                    required: true,
                    timeInInterval: {
                      time: pickerTimeInMinutes,
                      ranges: dayOfWeekRanges,
                    },
                  }"
                >
                  <VTextField
                    v-bind="{ ...attrs, ...ariaInput }"
                    :value="pickerTime"
                    :label="$t('bookingForm.selectTime')"
                    data-test="timeTextField"
                    prepend-icon="mdi-clock-outline"
                    :hide-details="!errors.length"
                    :error="!!errors.length"
                    :errorMessages="$t(errors[0])"
                    readonly
                    v-on="on"
                  />
                </ValidationProvider>
              </template>
              <VTimePicker
                v-if="timePickerMenuOpen"
                v-model="pickerTime"
                format="24hr"
                color="brandingPrimary"
                data-test="timePicker"
                :allowed-hours="allowedHours"
                :allowed-minutes="allowedMinutes"
                :min="minTime"
                :max="maxTime"
                full-width
                @click:minute="updateMinutesPicker"
                @click:hour="updateTime"
              />
            </VMenu>
          </VCol>
        </VRow>
        <VRow
          v-if="!isWorkingHoursRoundTheClock"
          class="dialog-row pb-4 pl-8 my-0"
        >
          <VCol cols="12" class="pl-2 d-flex flex-column">
            <span class="body-1">{{
              $t("bookingForm.dateSelection.workingHoursForDate", {
                date: formattedDate,
              })
            }}</span>
            <div class="d-flex flex-column">
              <span
                v-for="(range, index) in timeRanges"
                :key="index"
                class="body-bold"
              >
                {{ range }}
              </span>
            </div>
          </VCol>
        </VRow>
      </template>
      <slot name="customBottom" :invalid="invalid">
        <VRow class="ma-0 dialog-row px-6 pb-4">
          <VBtn
            color="brandingPrimary"
            width="100%"
            class="button"
            data-test="setDateAndTimeButton"
            :class="{ disabled: invalid }"
            @click="nextStep"
          >
            <span :class="buttonTextClasses">
              {{ $t("bookingForm.dateSelection.outwardDateNext") }}
            </span>
          </VBtn>
        </VRow>
      </slot>
    </ValidationObserver>
  </VCard>
</template>
<script lang="ts">
export default {
  name: 'CustomDateSelect',
};
</script>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';
import SConstans from '@slg/web-customer-shared/src/utils/constants';
import TimeRangeLimiterEnum from '@slg/web-customer-shared/src/enum/TimeRangeLimiterEnum';
import CustomBtnToggle from '@slg/web-customer-shared/src/components/common/CustomBtnToggle.vue';
import { useI18n, useRouteRef, useVuetify } from '../../../composables';

const props = defineProps({
  minDate: {
    type: String,
    default: '',
  },
  maxDate: {
    type: String,
    default: '',
  },
  minTime: {
    type: String,
    default: '',
  },
  maxTime: {
    type: String,
    default: '',
  },
  time: {
    type: String,
    default: '',
  },
  date: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  isDialogOpen: {
    type: Boolean,
    default: null,
  },
  isRoundTrip: {
    type: Boolean,
    default: true,
  },
  workingHoursZoneWeekDays: {
    type: Array,
    default: () => [],
  },
  holidays: {
    type: Array,
    default: () => [],
  },
  timeZone: {
    type: String,
    default: null,
  },
  buttonTextClasses: {
    type: String,
    default: 'textWhite--text',
  },
});

const emit = defineEmits(['closeDialog', 'updateDate', 'updateDateType', 'updateTime', 'nextStep']);

const { t } = useI18n();
const route = useRouteRef();
const vuetify = useVuetify();

const datesSelection = ref(null);

const dateTypeItems = [
  {
    text: t('bookingForm.dateSelection.DEPARTURE_TIME'),
    value: 'DEPARTURE_TIME',
  },
  {
    text: t('bookingForm.dateSelection.ARRIVAL_TIME'),
    value: 'ARRIVAL_TIME',
  },
];
const weekEnd = ref(null);
const pickerTime = ref(null);
const hours = ref(null);
const timePickerMenuOpen = ref(false);
const holidayDay = undefined;

const getCurrentLang = computed(() => route.value.params.lang);

const isCustomHeightDatePickerHeight = computed(() => getCurrentLang.value !== 'en' && !props.date);

const formattedDate = computed(() => STimeConverter.convertToMonthDayFormatWithoutComma(
  props.date,
  route.value.params.lang
));

const dayOfWeek = computed(() => props.workingHoursZoneWeekDays[
  STimeConverter.convertToDayOfWeek(props.date)
]);

const holiday = computed(() => props.holidays.find(
  ({ date }) =>
    date ===
    STimeConverter.convertDateToFormat(
      props.date,
      props.timeZone,
      SConstans.TIME_FORMAT.YYYY_MM_DD
    )
));

const isWorkingHoursRoundTheClock = computed(() => (
  !holiday.value &&
  dayOfWeek.value.type === TimeRangeLimiterEnum.ROUND_THE_CLOCK
));

const pickerTimeInMinutes = computed(() => STimeConverter.convertTimeToMinutes(pickerTime.value));

const dayOfWeekRanges = computed(() => {
  if (holiday.value && holiday.value?.availabilityPeriods?.length) {
    return holiday.value.availabilityPeriods.map(({ from, to }) => ({
      fromMinutes: STimeConverter.convertTimeToMinutes(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          from,
          props.timeZone
        )
      ),
      toMinutes: STimeConverter.convertTimeToMinutes(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          to,
          props.timeZone
        )
      ),
    }));
  }
  return dayOfWeek.value.timeRanges;
});

const timeRanges = computed(() => {
  if (
    !holiday.value &&
    dayOfWeek.value.type === TimeRangeLimiterEnum.ROUND_THE_CLOCK
  ) {
    return ['00:00-00:00'];
  }

  if (holiday.value && holiday.value.availabilityPeriods.length) {
    return holiday.value.availabilityPeriods.reduce((acc, range) => {
      const from = STimeConverter.convertMinutesToTime(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          range.from,
          props.timeZone
        )
      );
      const to = STimeConverter.convertMinutesToTime(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          range.to,
          props.timeZone
        )
      );
      acc.push(`${from}-${to}`);
      return acc;
    }, []);
  }

  if (
    !holiday.value &&
    dayOfWeek.value.type === TimeRangeLimiterEnum.CUSTOM_HOURS
  ) {
    return dayOfWeek.value.timeRanges.reduce((acc, range) => {
      const from = STimeConverter.convertMinutesToTime(range.fromMinutes);
      const to = STimeConverter.convertMinutesToTime(range.toMinutes);
      acc.push(`${from}-${to}`);
      return acc;
    }, []);
  }
  return [];
});

function findOffDays() {
  weekEnd.value = props.workingHoursZoneWeekDays.reduce(
    (results, { type }, index) => {
      if (type === TimeRangeLimiterEnum.OFF_DAY) {
        results.push(index);
      }
      return results;
    },
    []
  );
}

pickerTime.value = props.time;
findOffDays();

function allowedDates(val) {
  if (!props.holidays.length) {
    // date is allowed if no holidays
    // and no weekEnd days or current day is not weekend day
    return (
      !weekEnd.value?.length ||
      weekEnd.value.every(
        (day) => day !== STimeConverter.convertToDayOfWeek(val)
      )
    );
  }
  const holidayItem = props.holidays.find(({ date }) => val === date);
  if (holidayItem && holidayItem.availabilityPeriods.length) {
    return true;
  }
  if (holidayItem && !holidayItem.availabilityPeriods.length) {
    return false;
  }
  return weekEnd.value.every(
    (day) => day !== STimeConverter.convertToDayOfWeek(val)
  );
}

function allowedHours(val) {
  if (holiday.value) {
    const hoursInMinutes = val * 60;
    return holiday.value.availabilityPeriods.some(({ from, to }) => {
      const fromMinutes = STimeConverter.convertTimeToMinutes(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          from,
          props.timeZone
        )
      );

      const toMinutes = STimeConverter.convertTimeToMinutes(
        STimeConverter.convertMilliSecondsToHoursAndMinutes(
          to,
          props.timeZone
        )
      );

      const fromHoursInMinutesWithoutExtraMinutes =
        Math.floor(fromMinutes / 60) * 60;
      return toMinutes
        ? hoursInMinutes >= fromHoursInMinutesWithoutExtraMinutes &&
            hoursInMinutes <= toMinutes
        : hoursInMinutes >= fromHoursInMinutesWithoutExtraMinutes &&
            hoursInMinutes <= 1440;
    });
  }

  if (
    !holidayDay &&
    dayOfWeek.value.type === TimeRangeLimiterEnum.ROUND_THE_CLOCK
  ) {
    return true;
  }

  if (
    !holidayDay &&
    dayOfWeek.value.type !== TimeRangeLimiterEnum.ROUND_THE_CLOCK
  ) {
    const hoursInMinutes = val * 60;
    return dayOfWeek.value.timeRanges.some(({ fromMinutes, toMinutes }) => {
      const fromHoursInMinutesWithoutExtraMinutes =
        Math.floor(fromMinutes / 60) * 60;
      return (
        hoursInMinutes >= fromHoursInMinutesWithoutExtraMinutes &&
        hoursInMinutes <= toMinutes
      );
    });
  }
}

function allowedMinutes(val) {
  const timeInMinutes = Number(hours.value) * 60 + val;

  if (holiday.value && holiday.value.availabilityPeriods.length) {
    return (
      holiday.value.availabilityPeriods.some(({ from, to }) => {
        const fromMinutes = STimeConverter.convertTimeToMinutes(
          STimeConverter.convertMilliSecondsToHoursAndMinutes(
            from,
            props.timeZone
          )
        );
        const toMinutes = STimeConverter.convertTimeToMinutes(
          STimeConverter.convertMilliSecondsToHoursAndMinutes(
            to,
            props.timeZone
          )
        );
        return toMinutes
          ? timeInMinutes >= fromMinutes && timeInMinutes <= toMinutes
          : timeInMinutes >= fromMinutes && timeInMinutes <= 1440;
      }) && val % 5 === 0
    );
  }

  if (
    (holiday.value && !holiday.value.availabilityPeriods.length) ||
    (dayOfWeek.value.type === TimeRangeLimiterEnum.ROUND_THE_CLOCK &&
      val % 5 === 0)
  ) {
    return true;
  }

  if (!holiday.value) {
    return (
      dayOfWeek.value.timeRanges.some(
        ({ fromMinutes, toMinutes }) =>
          timeInMinutes >= fromMinutes && timeInMinutes <= toMinutes
      ) && val % 5 === 0
    );
  }
}

function updateDate(newValue) {
  emit('updateDate', newValue);
  if (vuetify.breakpoint.xs) {
    setTimeout(() => {
      datesSelection.value.$el.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'center',
      });
    }, 100);
  }
}

function updateDateType(newValue) {
  emit('updateDateType', newValue);
}

function updateTime(val) {
  hours.value = val;
  nextTick(() => {
    emit('updateTime', pickerTime.value);
  });
}

function updateMinutesPicker() {
  updateTime();
  timePickerMenuOpen.value = false;
}

function nextStep() {
  emit('nextStep', 'ReturnDateSelection');
}
</script>
<style lang="scss" scoped>
@import "@slg/web-customer-shared/src/style/variables.scss";
.dialog-row {
  width: 100%;
}

.datepicker {
  .v-date-picker-title {
    height: 140px !important;
  }
}

.date__picker-custom-header {
  margin-top: 7px;
  margin-right: 2px;
  position: absolute;
  top: 28px;
  left: 14px;
  right: 14px;
  font-size: $fs-32;
  line-height: 133%;
  color: var(--v-textWhite-base) !important;
}

.close-button {
  line-height: 12px;
  font-size: $fs-8;
  margin-top: 7px;
  margin-right: 2px;
  position: absolute;
  top: 0;
  right: 0;
}
</style>
